import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";

export default function ProductGallery({ images = [], alt, onOpen, className = "", grayscale = false, testid = "gallery", fit = "cover", jumpTo = null }) {
  const [idx, setIdx] = useState(0);
  const touch = useRef(null);
  const n = images.length;
  useEffect(() => { if (jumpTo != null && jumpTo >= 0 && jumpTo < n) setIdx(jumpTo); }, [jumpTo, n]);
  const go = (d, e) => { e?.stopPropagation(); setIdx((i) => (i + d + n) % n); };
  if (!n) return <div className={`bg-pb-elevated flex items-center justify-center text-zinc-700 text-xs uppercase tracking-widest ${className}`} data-testid={testid}>No photo</div>;
  return (
    <div className={`relative overflow-hidden bg-pb-elevated group/gal ${className}`} data-testid={testid}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { if (touch.current == null) return; const dx = e.changedTouches[0].clientX - touch.current; if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); touch.current = null; }}>
      <div className="flex h-full w-full transition-transform duration-300 ease-out" style={{ transform: `translateX(-${idx * 100}%)` }}>
        {images.map((src, i) => (
          <img key={i} src={src} alt={`${alt} ${i + 1}`} draggable={false}
            onClick={() => onOpen?.(i)}
            className={`w-full h-full shrink-0 ${fit === "contain" ? "object-contain" : "object-cover"} ${onOpen ? "cursor-zoom-in" : ""} ${grayscale ? "grayscale" : ""}`}
            data-testid={`${testid}-img-${i}`} />
        ))}
      </div>
      {onOpen && (
        <button onClick={() => onOpen(idx)} data-testid={`${testid}-expand`} title="Enlarge"
          className="absolute bottom-2 right-2 bg-black/70 border border-white/20 text-white p-1.5 opacity-0 group-hover/gal:opacity-100 transition-opacity">
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      )}
      {n > 1 && (
        <>
          <button onClick={(e) => go(-1, e)} data-testid={`${testid}-prev`} aria-label="Previous photo"
            className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-pb-red hover:text-black text-white p-1.5 border border-white/10 transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={(e) => go(1, e)} data-testid={`${testid}-next`} aria-label="Next photo"
            className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-pb-red hover:text-black text-white p-1.5 border border-white/10 transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1" data-testid={`${testid}-dots`}>
            {images.map((_, i) => (
              <button key={i} onClick={(e) => { e.stopPropagation(); setIdx(i); }} aria-label={`Photo ${i + 1}`}
                className={`h-1.5 transition-all ${i === idx ? "w-5 bg-pb-red" : "w-1.5 bg-white/40 hover:bg-white/80"}`} />
            ))}
          </div>
          <span className="absolute top-2 left-2 bg-black/70 text-[9px] font-mono text-zinc-300 px-1.5 py-0.5" data-testid={`${testid}-counter`}>{idx + 1}/{n}</span>
        </>
      )}
    </div>
  );
}
